/**
 * Chunking Module
 *
 * Handles splitting large text into chunks for Supabase Broadcast
 * and reassembling chunks back into complete text.
 *
 * Uses UTF-8 byte length calculations to respect message size limits.
 */

import { MAX_BROADCAST_BYTES, CHUNK_BYTE_TARGET, MAX_CHUNKS } from './protocol';

/**
 * Result of chunking operation
 */
export interface ChunkingResult {
  /** Whether the text was chunked */
  chunked: boolean;
  /** Array of chunks (single element if not chunked) */
  chunks: string[];
  /** Total byte length of original text */
  totalBytes: number;
  /** Number of chunks */
  chunkCount: number;
}

/**
 * Result of reassembly operation
 */
export interface ReassemblyResult {
  /** Reassembled text */
  text: string;
  /** Total byte length */
  totalBytes: number;
  /** Whether reassembly was successful */
  success: boolean;
  /** Error message if unsuccessful */
  error?: string;
}

/**
 * Calculate UTF-8 byte length of a string.
 *
 * @param str - String to measure
 * @returns Byte length in UTF-8 encoding
 */
export function getUtf8ByteLength(str: string): number {
  // Use TextEncoder for accurate UTF-8 byte counting
  return new TextEncoder().encode(str).length;
}

/**
 * Check if text needs chunking based on byte size.
 *
 * @param text - Text to check
 * @returns true if text exceeds MAX_BROADCAST_BYTES
 */
export function needsChunking(text: string): boolean {
  return getUtf8ByteLength(text) > MAX_BROADCAST_BYTES;
}

/**
 * Check if text can be transferred (within max size limits).
 *
 * @param text - Text to check
 * @returns true if text is within transferable limits
 */
export function canTransfer(text: string): boolean {
  const bytes = getUtf8ByteLength(text);
  const maxBytes = MAX_CHUNKS * CHUNK_BYTE_TARGET;
  return bytes <= maxBytes;
}

/**
 * Get the maximum transferable byte size.
 *
 * @returns Maximum bytes that can be transferred via chunking
 */
export function getMaxTransferBytes(): number {
  return MAX_CHUNKS * CHUNK_BYTE_TARGET;
}

/**
 * Split text into chunks for transfer.
 *
 * Chunks are split at character boundaries to avoid breaking
 * multi-byte UTF-8 characters.
 *
 * @param text - Text to chunk
 * @returns ChunkingResult with chunks array
 * @throws Error if text exceeds maximum transferable size
 */
export function chunkText(text: string): ChunkingResult {
  const totalBytes = getUtf8ByteLength(text);

  // Check if within single-message limit
  if (totalBytes <= MAX_BROADCAST_BYTES) {
    return {
      chunked: false,
      chunks: [text],
      totalBytes,
      chunkCount: 1,
    };
  }

  // Check if within max transfer limit
  const maxBytes = getMaxTransferBytes();
  if (totalBytes > maxBytes) {
    throw new Error(
      `Text too large: ${totalBytes} bytes exceeds maximum ${maxBytes} bytes`
    );
  }

  // Split into chunks
  const chunks: string[] = [];
  const encoder = new TextEncoder();
  let currentChunk = '';
  let currentBytes = 0;

  for (const char of text) {
    const charBytes = encoder.encode(char).length;

    // If adding this char would exceed target, start new chunk
    if (currentBytes + charBytes > CHUNK_BYTE_TARGET && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = char;
      currentBytes = charBytes;
    } else {
      currentChunk += char;
      currentBytes += charBytes;
    }
  }

  // Push final chunk
  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  // Verify we don't exceed max chunks
  if (chunks.length > MAX_CHUNKS) {
    throw new Error(
      `Too many chunks: ${chunks.length} exceeds maximum ${MAX_CHUNKS}`
    );
  }

  return {
    chunked: true,
    chunks,
    totalBytes,
    chunkCount: chunks.length,
  };
}

/**
 * Reassemble chunks into complete text.
 *
 * @param chunks - Array of chunks in order
 * @param expectedBytes - Optional expected total byte count for validation
 * @returns ReassemblyResult with reassembled text
 */
export function reassembleChunks(
  chunks: string[],
  expectedBytes?: number
): ReassemblyResult {
  if (chunks.length === 0) {
    return {
      text: '',
      totalBytes: 0,
      success: true,
    };
  }

  const text = chunks.join('');
  const totalBytes = getUtf8ByteLength(text);

  // Validate byte count if expected
  if (expectedBytes !== undefined && totalBytes !== expectedBytes) {
    return {
      text: '',
      totalBytes,
      success: false,
      error: `Byte count mismatch: got ${totalBytes}, expected ${expectedBytes}`,
    };
  }

  return {
    text,
    totalBytes,
    success: true,
  };
}

/**
 * Chunk collector for receiving chunks out of order.
 * Tracks received chunks and validates completeness.
 */
export class ChunkCollector {
  private chunks: Map<number, string> = new Map();
  private expectedCount: number;
  private expectedBytes: number;

  /**
   * Create a new chunk collector.
   *
   * @param expectedCount - Expected number of chunks
   * @param expectedBytes - Expected total byte count
   */
  constructor(expectedCount: number, expectedBytes: number) {
    this.expectedCount = expectedCount;
    this.expectedBytes = expectedBytes;
  }

  /**
   * Add a chunk to the collector.
   *
   * @param index - Chunk index (0-based)
   * @param chunk - Chunk content
   * @returns true if chunk was added (not duplicate)
   */
  addChunk(index: number, chunk: string): boolean {
    if (this.chunks.has(index)) {
      return false; // Duplicate
    }
    if (index < 0 || index >= this.expectedCount) {
      return false; // Out of range
    }
    this.chunks.set(index, chunk);
    return true;
  }

  /**
   * Get the number of received chunks.
   */
  get receivedCount(): number {
    return this.chunks.size;
  }

  /**
   * Check if all chunks have been received.
   */
  get isComplete(): boolean {
    return this.chunks.size === this.expectedCount;
  }

  /**
   * Get indices of missing chunks.
   */
  getMissingIndices(): number[] {
    const missing: number[] = [];
    for (let i = 0; i < this.expectedCount; i++) {
      if (!this.chunks.has(i)) {
        missing.push(i);
      }
    }
    return missing;
  }

  /**
   * Reassemble all chunks into complete text.
   *
   * @returns ReassemblyResult, or error if incomplete
   */
  reassemble(): ReassemblyResult {
    if (!this.isComplete) {
      const missing = this.getMissingIndices();
      return {
        text: '',
        totalBytes: 0,
        success: false,
        error: `Missing chunks: ${missing.join(', ')}`,
      };
    }

    // Collect chunks in order
    const orderedChunks: string[] = [];
    for (let i = 0; i < this.expectedCount; i++) {
      orderedChunks.push(this.chunks.get(i)!);
    }

    return reassembleChunks(orderedChunks, this.expectedBytes);
  }

  /**
   * Reset the collector for reuse.
   *
   * @param expectedCount - New expected chunk count
   * @param expectedBytes - New expected byte count
   */
  reset(expectedCount: number, expectedBytes: number): void {
    this.chunks.clear();
    this.expectedCount = expectedCount;
    this.expectedBytes = expectedBytes;
  }
}
